// skills/crafting/crafting.js
// Crafting skill logic

import { JEWELRY, TOOLS, ALL_ARMOR, ARMOR_BY_TIER, ARMOR_TIERS, ALL_MAGIC_ARMOR, MAGIC_ARMOR_BY_TIER, MAGIC_ARMOR_TIERS } from './craftingData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { getItemName } from '../../data/items/index.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { rollForCorruption, isGoldCostWaived } from '../../systems/corruption.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { getTalentLootBonus, getTalentFlatBonus, rollTalentProc } from '../../systems/talentTree/talentTree.js';
import { getItem } from '../../data/items/index.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

class CraftingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.CRAFTING, TOOLS, 'NEEDLE');
    }

    /**
     * Get the currently equipped tool object (alias for BaseSkill method)
     */
    get currentTool() {
        return this.getCurrentTool();
    }

    /**
     * Roll for talent proc effects and apply them
     * Returns object with proc results
     */
    rollCraftingProcs() {
        const procs = {
            materialSave: false,
            goldWaive: false,
            doubleOutput: false,
            bulkProduce: false,
            patronsFavor: false,
            inspiredCraft: false
        };

        // Material save - 15% chance
        if (rollTalentProc(SKILLS.CRAFTING, 'materialSave')) {
            procs.materialSave = true;
        }

        // Gold waive - 7.5% per rank
        if (rollTalentProc(SKILLS.CRAFTING, 'goldWaive')) {
            procs.goldWaive = true;
        }

        // Double output - 5% per rank
        if (rollTalentProc(SKILLS.CRAFTING, 'doubleOutput')) {
            procs.doubleOutput = true;
        }

        // Bulk produce - 10% chance for 2 extra items
        if (rollTalentProc(SKILLS.CRAFTING, 'bulkProduce')) {
            procs.bulkProduce = true;
        }

        // Patron's Favor - 5% per rank for gold tip
        if (rollTalentProc(SKILLS.CRAFTING, 'patronsFavor')) {
            procs.patronsFavor = true;
        }

        // Inspired craft - 10% chance for double XP
        if (rollTalentProc(SKILLS.CRAFTING, 'inspiredCraft')) {
            procs.inspiredCraft = true;
        }

        return procs;
    }

    stopCrafting() {
        gameState.clearAction();
    }

    /**
     * Purchase or equip a tool
     */
    upgradeTool(toolId) {
        const tool = Object.values(TOOLS).find(t => t.id === toolId);
        if (!tool) return { success: false, reason: 'Invalid tool' };

        const skill = gameState.getSkill(SKILLS.CRAFTING);
        if (skill.level < tool.level) {
            return { success: false, reason: `Requires level ${tool.level} Crafting` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.CRAFTING, toolId)) {
            // Just equip it
            gameState.equipTool(SKILLS.CRAFTING, toolId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.CRAFTING, toolId, tool.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.CRAFTING, toolId);
        return { success: true, action: 'purchased' };
    }

    // ========== JEWELRY CRAFTING ==========

    canCraftJewelry(itemId) {
        const item = Object.values(JEWELRY).find(i => i.id === itemId);
        if (!item) return { success: false, reason: 'Item not found' };

        const skill = gameState.getSkill(SKILLS.CRAFTING);
        if (skill.level < item.level) {
            return { success: false, reason: `Requires level ${item.level} Crafting` };
        }

        // Check all required materials
        if (item.inputItems) {
            for (const input of item.inputItems) {
                const count = gameState.getItemCount(input.itemId);
                if (count < input.quantity) {
                    const itemName = getItemName(input.itemId);
                    return {
                        success: false,
                        reason: `Need ${input.quantity} ${itemName} (have ${count})`
                    };
                }
            }
        }

        // Check if output can fit in inventory
        const outputItem = item.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startCraftingJewelry(itemId, showToast = true) {
        const item = Object.values(JEWELRY).find(i => i.id === itemId);
        const canCraft = this.canCraftJewelry(itemId);

        if (!canCraft.success) {
            if (showToast) {
                toastWarning(canCraft.reason);
            }
            logger.debug('Cannot craft jewelry:', canCraft.reason);
            return false;
        }

        // Calculate crafting time including talent bonuses
        const baseTime = item.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const craftingTime = baseTime / speedMultiplier;

        const action = {
            type: 'crafting',
            subType: 'jewelry',
            name: `Crafting ${item.name}`,
            item: item,
            itemId: itemId,
            isJewelry: true,
            progress: 0,
            duration: craftingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeCraftingJewelry(item);
                    const canContinue = this.canCraftJewelry(itemId);
                    if (canContinue.success) {
                        this.startCraftingJewelry(itemId, false);
                    } else {
                        toastWarning(canContinue.reason);
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeCraftingJewelry(item);
                    const canContinue = this.canCraftJewelry(itemId);
                    if (canContinue.success) {
                        this.startCraftingJewelry(itemId, false);
                    } else {
                        toastWarning(canContinue.reason);
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    completeCraftingJewelry(item) {
        // Roll for talent procs first
        const procs = this.rollCraftingProcs();

        // Get output item ID for proc notifications
        const outputItemId = item.drops?.[0]?.itemId;

        // Consume input materials (unless material save proc)
        if (!procs.materialSave) {
            if (item.inputItems) {
                for (const input of item.inputItems) {
                    gameState.removeItem(input.itemId, input.quantity);
                }
            }
        } else {
            recordTalentProc(SKILLS.CRAFTING, 'Material Efficiency', 'Materials saved!', null);
        }

        // Calculate XP with bonuses
        let xpAmount = item.xp;

        // Artisan's Pride - +25% XP for highest tier items
        const highTierBonus = getTalentFlatBonus(SKILLS.CRAFTING, 'highTierXPBonus');
        if (highTierBonus > 0 && this.isHighestTierJewelry(item)) {
            xpAmount *= (1 + highTierBonus);
        }

        // Inspired Creation - double XP
        if (procs.inspiredCraft) {
            xpAmount *= 2;
            recordTalentProc(SKILLS.CRAFTING, 'Inspired Creation', 'Double XP!', null);
        }

        const xpResult = addXP(SKILLS.CRAFTING, xpAmount, item.id);

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.CRAFTING, item.drops);

        // Apply talent proc bonuses
        if (procs.doubleOutput && outputItemId) {
            awardDrops(SKILLS.CRAFTING, item.drops);
            recordTalentProc(SKILLS.CRAFTING, 'Masterwork', null, null, { itemId: outputItemId, quantity: 1 });
        }

        if (procs.bulkProduce && outputItemId) {
            awardDrops(SKILLS.CRAFTING, item.drops);
            awardDrops(SKILLS.CRAFTING, item.drops);
            recordTalentProc(SKILLS.CRAFTING, 'Bulk Produce', null, null, { itemId: outputItemId, quantity: 2 });
        }

        // Patron's Favor - gold tip based on item value
        if (procs.patronsFavor) {
            this.awardPatronTip(item);
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.CRAFTING);

        // Check for pilferer contract assistance (processing)
        pilferingSkill.tryContractAssist('crafting');

        this.onCraftingComplete(item, xpResult);
    }

    getAvailableJewelry() {
        const skill = gameState.getSkill(SKILLS.CRAFTING);
        return Object.values(JEWELRY).filter(item => item.level <= skill.level);
    }

    // ========== ARMOR CRAFTING ==========

    /**
     * Get armor recipes for a specific tier
     */
    getArmorByTier(tier) {
        return ARMOR_BY_TIER[tier] ? Object.values(ARMOR_BY_TIER[tier]) : [];
    }

    /**
     * Get armor tier info for UI
     */
    getArmorTiers() {
        return ARMOR_TIERS;
    }

    /**
     * Check if player can craft armor piece
     * Checks: level requirement, hide materials, gold balance
     */
    canCraftArmor(itemId) {
        const item = Object.values(ALL_ARMOR).find(i => i.id === itemId);
        if (!item) return { success: false, reason: 'Armor not found' };

        const skill = gameState.getSkill(SKILLS.CRAFTING);
        if (skill.level < item.level) {
            return { success: false, reason: `Requires level ${item.level} Crafting` };
        }

        // Check hide materials
        if (item.inputItems) {
            for (const input of item.inputItems) {
                const count = gameState.getItemCount(input.itemId);
                if (count < input.quantity) {
                    const itemName = getItemName(input.itemId);
                    return {
                        success: false,
                        reason: `Need ${input.quantity} ${itemName} (have ${count})`
                    };
                }
            }
        }

        // Check gold cost (skip if corruption waives it)
        if (item.goldCost && item.goldCost > 0 && !isGoldCostWaived(SKILLS.CRAFTING)) {
            const gold = gameState.getGold();
            if (gold < item.goldCost) {
                return {
                    success: false,
                    reason: `Need ${item.goldCost} gold (have ${gold})`
                };
            }
        }

        // Check if output can fit in inventory
        const outputItem = item.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    /**
     * Start crafting armor piece
     * Deducts gold upfront when crafting begins
     */
    startCraftingArmor(itemId, showToast = true) {
        const item = Object.values(ALL_ARMOR).find(i => i.id === itemId);
        const canCraft = this.canCraftArmor(itemId);

        if (!canCraft.success) {
            if (showToast) {
                toastWarning(canCraft.reason);
            }
            logger.debug('Cannot craft armor:', canCraft.reason);
            return false;
        }

        // Deduct gold cost upfront (skip if corruption waives it)
        if (item.goldCost && item.goldCost > 0 && !isGoldCostWaived(SKILLS.CRAFTING)) {
            gameState.removeGold(item.goldCost);
        }

        // Calculate crafting time including talent bonuses
        const baseTime = item.baseTime;
        let speedMultiplier = this.getEffectiveSpeedMultiplier();

        // Apply Master Tailor bonus for armor crafting
        const armorSpeedBonus = this.getArmorCraftSpeedBonus();
        if (armorSpeedBonus > 0) {
            speedMultiplier *= (1 + armorSpeedBonus);
        }

        const craftingTime = baseTime / speedMultiplier;

        const action = {
            type: 'crafting',
            subType: 'ranged',
            armorTier: item.tier,
            name: `Crafting ${item.name}`,
            item: item,
            itemId: itemId,
            isArmor: true,
            progress: 0,
            duration: craftingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeCraftingArmor(item);
                    const canContinue = this.canCraftArmor(itemId);
                    if (canContinue.success) {
                        this.startCraftingArmor(itemId, false);
                    } else {
                        toastWarning(canContinue.reason);
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeCraftingArmor(item);
                    const canContinue = this.canCraftArmor(itemId);
                    if (canContinue.success) {
                        this.startCraftingArmor(itemId, false);
                    } else {
                        toastWarning(canContinue.reason);
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    /**
     * Complete armor crafting
     * Consumes hide materials and awards XP + armor piece
     */
    completeCraftingArmor(item) {
        // Roll for talent procs first
        const procs = this.rollCraftingProcs();

        // Get output item ID for proc notifications
        const outputItemId = item.drops?.[0]?.itemId;

        // Consume hide materials (unless material save proc)
        if (!procs.materialSave) {
            if (item.inputItems) {
                for (const input of item.inputItems) {
                    gameState.removeItem(input.itemId, input.quantity);
                }
            }
        } else {
            recordTalentProc(SKILLS.CRAFTING, 'Material Efficiency', 'Materials saved!', null);
        }

        // Gold Waive - refund gold cost if proc fired (gold was deducted upfront in startCraftingArmor)
        if (procs.goldWaive && item.goldCost && item.goldCost > 0 && !isGoldCostWaived(SKILLS.CRAFTING)) {
            gameState.addGold(item.goldCost);
            recordTalentProc(SKILLS.CRAFTING, 'Thrifty Crafter', `${item.goldCost}g saved!`, null);
        }

        // Calculate XP with bonuses
        let xpAmount = item.xp;

        // Artisan's Pride - +25% XP for highest tier items
        const highTierBonus = getTalentFlatBonus(SKILLS.CRAFTING, 'highTierXPBonus');
        if (highTierBonus > 0 && this.isHighestTierArmor(item)) {
            xpAmount *= (1 + highTierBonus);
        }

        // Inspired Creation - double XP
        if (procs.inspiredCraft) {
            xpAmount *= 2;
            recordTalentProc(SKILLS.CRAFTING, 'Inspired Creation', 'Double XP!', null);
        }

        const xpResult = addXP(SKILLS.CRAFTING, xpAmount, item.id);

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.CRAFTING, item.drops);

        // Apply talent proc bonuses
        if (procs.doubleOutput && outputItemId) {
            awardDrops(SKILLS.CRAFTING, item.drops);
            recordTalentProc(SKILLS.CRAFTING, 'Masterwork', null, null, { itemId: outputItemId, quantity: 1 });
        }

        if (procs.bulkProduce && outputItemId) {
            awardDrops(SKILLS.CRAFTING, item.drops);
            awardDrops(SKILLS.CRAFTING, item.drops);
            recordTalentProc(SKILLS.CRAFTING, 'Bulk Produce', null, null, { itemId: outputItemId, quantity: 2 });
        }

        // Patron's Favor - gold tip based on item value
        if (procs.patronsFavor) {
            this.awardPatronTip(item);
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.CRAFTING);

        // Check for pilferer contract assistance (processing)
        pilferingSkill.tryContractAssist('crafting');

        this.onCraftingComplete(item, xpResult);
    }

    /**
     * Get available armor for player's current level
     */
    getAvailableArmor() {
        const skill = gameState.getSkill(SKILLS.CRAFTING);
        return Object.values(ALL_ARMOR).filter(item => item.level <= skill.level);
    }

    /**
     * Check if player has unlocked a specific armor tier
     */
    isTierUnlocked(tier) {
        const tierInfo = ARMOR_TIERS[tier];
        if (!tierInfo) return false;
        const skill = gameState.getSkill(SKILLS.CRAFTING);
        return skill.level >= tierInfo.level;
    }

    // ========== MAGIC ARMOR CRAFTING ==========

    /**
     * Get magic armor recipes for a specific tier
     */
    getMagicArmorByTier(tier) {
        return MAGIC_ARMOR_BY_TIER[tier] ? Object.values(MAGIC_ARMOR_BY_TIER[tier]) : [];
    }

    /**
     * Get magic armor tier info for UI
     */
    getMagicArmorTiers() {
        return MAGIC_ARMOR_TIERS;
    }

    /**
     * Check if player can craft magic armor piece
     * Checks: level requirement, cloth materials, gold balance
     */
    canCraftMagicArmor(itemId) {
        const item = Object.values(ALL_MAGIC_ARMOR).find(i => i.id === itemId);
        if (!item) return { success: false, reason: 'Magic armor not found' };

        const skill = gameState.getSkill(SKILLS.CRAFTING);
        if (skill.level < item.level) {
            return { success: false, reason: `Requires level ${item.level} Crafting` };
        }

        // Check cloth materials
        if (item.inputItems) {
            for (const input of item.inputItems) {
                const count = gameState.getItemCount(input.itemId);
                if (count < input.quantity) {
                    const itemName = getItemName(input.itemId);
                    return {
                        success: false,
                        reason: `Need ${input.quantity} ${itemName} (have ${count})`
                    };
                }
            }
        }

        // Check gold cost (skip if corruption waives it)
        if (item.goldCost && item.goldCost > 0 && !isGoldCostWaived(SKILLS.CRAFTING)) {
            const gold = gameState.getGold();
            if (gold < item.goldCost) {
                return {
                    success: false,
                    reason: `Need ${item.goldCost} gold (have ${gold})`
                };
            }
        }

        // Check if output can fit in inventory
        const outputItem = item.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    /**
     * Start crafting magic armor piece
     * Deducts gold upfront when crafting begins
     */
    startCraftingMagicArmor(itemId, showToast = true) {
        const item = Object.values(ALL_MAGIC_ARMOR).find(i => i.id === itemId);
        const canCraft = this.canCraftMagicArmor(itemId);

        if (!canCraft.success) {
            if (showToast) {
                toastWarning(canCraft.reason);
            }
            logger.debug('Cannot craft magic armor:', canCraft.reason);
            return false;
        }

        // Deduct gold cost upfront (skip if corruption waives it)
        if (item.goldCost && item.goldCost > 0 && !isGoldCostWaived(SKILLS.CRAFTING)) {
            gameState.removeGold(item.goldCost);
        }

        // Calculate crafting time including talent bonuses
        const baseTime = item.baseTime;
        let speedMultiplier = this.getEffectiveSpeedMultiplier();

        // Apply Master Tailor bonus for magic armor crafting
        const armorSpeedBonus = this.getArmorCraftSpeedBonus();
        if (armorSpeedBonus > 0) {
            speedMultiplier *= (1 + armorSpeedBonus);
        }

        const craftingTime = baseTime / speedMultiplier;

        const action = {
            type: 'crafting',
            subType: 'magic',
            armorTier: item.tier,
            name: `Crafting ${item.name}`,
            item: item,
            itemId: itemId,
            isMagicArmor: true,
            progress: 0,
            duration: craftingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeCraftingMagicArmor(item);
                    const canContinue = this.canCraftMagicArmor(itemId);
                    if (canContinue.success) {
                        this.startCraftingMagicArmor(itemId, false);
                    } else {
                        toastWarning(canContinue.reason);
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeCraftingMagicArmor(item);
                    const canContinue = this.canCraftMagicArmor(itemId);
                    if (canContinue.success) {
                        this.startCraftingMagicArmor(itemId, false);
                    } else {
                        toastWarning(canContinue.reason);
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    /**
     * Complete magic armor crafting
     * Consumes cloth materials and awards XP + armor piece
     */
    completeCraftingMagicArmor(item) {
        // Roll for talent procs first
        const procs = this.rollCraftingProcs();

        // Get output item ID for proc notifications
        const outputItemId = item.drops?.[0]?.itemId;

        // Consume cloth materials (unless material save proc)
        if (!procs.materialSave) {
            if (item.inputItems) {
                for (const input of item.inputItems) {
                    gameState.removeItem(input.itemId, input.quantity);
                }
            }
        } else {
            recordTalentProc(SKILLS.CRAFTING, 'Material Efficiency', 'Materials saved!', null);
        }

        // Gold Waive - refund gold cost if proc fired (gold was deducted upfront in startCraftingMagicArmor)
        if (procs.goldWaive && item.goldCost && item.goldCost > 0 && !isGoldCostWaived(SKILLS.CRAFTING)) {
            gameState.addGold(item.goldCost);
            recordTalentProc(SKILLS.CRAFTING, 'Thrifty Crafter', `${item.goldCost}g saved!`, null);
        }

        // Calculate XP with bonuses
        let xpAmount = item.xp;

        // Artisan's Pride - +25% XP for highest tier items
        const highTierBonus = getTalentFlatBonus(SKILLS.CRAFTING, 'highTierXPBonus');
        if (highTierBonus > 0 && this.isHighestTierMagicArmor(item)) {
            xpAmount *= (1 + highTierBonus);
        }

        // Inspired Creation - double XP
        if (procs.inspiredCraft) {
            xpAmount *= 2;
            recordTalentProc(SKILLS.CRAFTING, 'Inspired Creation', 'Double XP!', null);
        }

        const xpResult = addXP(SKILLS.CRAFTING, xpAmount, item.id);

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.CRAFTING, item.drops);

        // Apply talent proc bonuses
        if (procs.doubleOutput && outputItemId) {
            awardDrops(SKILLS.CRAFTING, item.drops);
            recordTalentProc(SKILLS.CRAFTING, 'Masterwork', null, null, { itemId: outputItemId, quantity: 1 });
        }

        if (procs.bulkProduce && outputItemId) {
            awardDrops(SKILLS.CRAFTING, item.drops);
            awardDrops(SKILLS.CRAFTING, item.drops);
            recordTalentProc(SKILLS.CRAFTING, 'Bulk Produce', null, null, { itemId: outputItemId, quantity: 2 });
        }

        // Patron's Favor - gold tip based on item value
        if (procs.patronsFavor) {
            this.awardPatronTip(item);
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.CRAFTING);

        // Check for pilferer contract assistance (processing)
        pilferingSkill.tryContractAssist('crafting');

        this.onCraftingComplete(item, xpResult);
    }

    /**
     * Get available magic armor for player's current level
     */
    getAvailableMagicArmor() {
        const skill = gameState.getSkill(SKILLS.CRAFTING);
        return Object.values(ALL_MAGIC_ARMOR).filter(item => item.level <= skill.level);
    }

    /**
     * Check if player has unlocked a specific magic armor tier
     */
    isMagicTierUnlocked(tier) {
        const tierInfo = MAGIC_ARMOR_TIERS[tier];
        if (!tierInfo) return false;
        const skill = gameState.getSkill(SKILLS.CRAFTING);
        return skill.level >= tierInfo.level;
    }

    // ========== TALENT HELPER METHODS ==========

    /**
     * Check if item is the highest tier jewelry player can currently craft
     * For Artisan's Pride talent bonus
     */
    isHighestTierJewelry(item) {
        const skill = gameState.getSkill(SKILLS.CRAFTING);
        const allJewelry = Object.values(JEWELRY);
        const maxUnlockedLevel = Math.max(...allJewelry.filter(j => j.level <= skill.level).map(j => j.level));
        return item.level === maxUnlockedLevel;
    }

    /**
     * Check if item is the highest tier armor player can currently craft
     * For Artisan's Pride talent bonus
     */
    isHighestTierArmor(item) {
        const skill = gameState.getSkill(SKILLS.CRAFTING);
        const allArmor = Object.values(ALL_ARMOR);
        const maxUnlockedLevel = Math.max(...allArmor.filter(a => a.level <= skill.level).map(a => a.level));
        return item.level === maxUnlockedLevel;
    }

    /**
     * Check if item is the highest tier magic armor player can currently craft
     * For Artisan's Pride talent bonus
     */
    isHighestTierMagicArmor(item) {
        const skill = gameState.getSkill(SKILLS.CRAFTING);
        const allMagicArmor = Object.values(ALL_MAGIC_ARMOR);
        const maxUnlockedLevel = Math.max(...allMagicArmor.filter(a => a.level <= skill.level).map(a => a.level));
        return item.level === maxUnlockedLevel;
    }

    /**
     * Award gold tip from Patron's Favor talent
     * Gives 25% of the crafted item's value as bonus gold
     */
    awardPatronTip(item) {
        // Get the output item's value
        const outputItemId = item.drops?.[0]?.itemId;
        if (!outputItemId) return;

        const outputItem = getItem(outputItemId);
        if (!outputItem || !outputItem.value) return;

        const tipAmount = Math.floor(outputItem.value * 0.25);
        if (tipAmount > 0) {
            gameState.addGold(tipAmount);
            recordTalentProc(SKILLS.CRAFTING, "Patron's Favor", `+${tipAmount} gold tip`, null);
        }
    }

    /**
     * Get armor crafting speed bonus from Master Tailor talent
     */
    getArmorCraftSpeedBonus() {
        return getTalentFlatBonus(SKILLS.CRAFTING, 'armorCraftSpeed');
    }

    onCraftingComplete(item, xpResult) {
        logger.game(`Crafted ${item.name}! +${item.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Crafting level up! Now level ${xpResult.newLevel}`);
        }
    }
}

export const craftingSkill = new CraftingSkill();
export default craftingSkill;